import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  Hotel, Plus, X, Save, Loader2, Edit3, Trash2, Euro, Sun, Snowflake, Leaf,
  Users, CalendarDays, Coffee, UtensilsCrossed, Cookie, AlertTriangle, TrendingUp,
  Package, Bus, BarChart3, Receipt, Wallet, Target, PiggyBank, Sparkles, BedDouble,
} from "lucide-react";
import api, { formatApiError } from "../api";
import { formatEUR as fmtEur } from "../utils/format";
import {
  Button, Card, Input, Select, Label, Badge, Spinner, EmptyState,
} from "../components/ui/Primitives";

const SEASON_ICON = { Summer: Sun, Winter: Snowflake, Shoulder: Leaf };

function Kpi({ label, value, sub, tone = "default", testId }) {
  const accent = tone === "ok" ? "border-ok/30" : tone === "warn" ? "border-warn/30" : tone === "err" ? "border-err/30" : "border-white/10";
  return (
    <div className={`bg-bg-surface border ${accent} rounded-xl px-4 py-3`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-ink-muted font-medium">{label}</div>
      <div className="num text-xl font-medium text-ink-primary tracking-tight mt-1">{value}</div>
      {sub && <div className="text-[10px] text-ink-muted mt-0.5">{sub}</div>}
    </div>
  );
}

// ============================================================
//  Package list + builder
// ============================================================
function MealEditor({ title, icon: Icon, value, onChange, recipes, allowWasteFactor = true, allowJause = false, meta }) {
  const { t } = useTranslation();
  const toggleRecipe = (rid) => {
    const cur = value.recipe_ids || [];
    onChange({ ...value, recipe_ids: cur.includes(rid) ? cur.filter((x) => x !== rid) : [...cur, rid] });
  };
  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon size={14} className="text-brand" />
          <div className="text-sm font-medium text-ink-primary">{title}</div>
        </div>
        {allowJause && (
          <Select value={value.kind || "Buffet"} onChange={(e) => onChange({ ...value, kind: e.target.value })} className="w-36" data-testid="jause-kind">
            {meta.jause_kinds.map((k) => <option key={k} value={k}>{t(`hotel.jause_${k}`, k)}</option>)}
          </Select>
        )}
      </div>
      {/* Recipe chips */}
      <div className="max-h-44 overflow-y-auto space-y-1 border border-white/5 rounded-md p-2">
        {recipes.length === 0 ? (
          <div className="text-xs text-ink-muted text-center py-3">{t("hotel.no_recipes")}</div>
        ) : recipes.map((r) => {
          const selected = (value.recipe_ids || []).includes(r.id);
          return (
            <button
              key={r.id}
              type="button"
              onClick={() => toggleRecipe(r.id)}
              className={`w-full flex items-center justify-between text-left px-2 py-1.5 rounded text-xs ${selected ? "bg-brand/15 text-ink-primary" : "hover:bg-white/[0.03] text-ink-secondary"}`}
              data-testid={`meal-recipe-${r.id}`}
            >
              <span className="truncate">{r.name}</span>
              <span className="text-ink-muted">{r.category}</span>
            </button>
          );
        })}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {allowWasteFactor && (
          <div>
            <Label className="text-[10px]">{t("hotel.waste_factor")}</Label>
            <Input
              type="number" step="1" min="0" max="100"
              value={value.waste_factor_pct ?? ""}
              onChange={(e) => onChange({ ...value, waste_factor_pct: e.target.value === "" ? null : Number(e.target.value) })}
              placeholder={`Default ${meta.defaults?.waste_factor_pct ?? 15}%`}
              data-testid={`waste-${title}`}
            />
          </div>
        )}
        <div>
          <Label className="text-[10px]">{t("hotel.per_guest_factor")}</Label>
          <Input
            type="number" step="0.1" min="0"
            value={value.per_guest_factor ?? "1"}
            onChange={(e) => onChange({ ...value, per_guest_factor: Number(e.target.value || 1) })}
          />
        </div>
        {allowJause && value.kind === "ToGo" && (
          <div className="col-span-2">
            <Label className="text-[10px]">{t("hotel.packaging_cost")}</Label>
            <Input
              type="number" step="0.01" min="0"
              value={value.packaging_cost_override ?? ""}
              onChange={(e) => onChange({ ...value, packaging_cost_override: e.target.value === "" ? null : Number(e.target.value) })}
              placeholder={`Default ${fmtEur(meta.defaults?.to_go_packaging_cost ?? 0.5)}`}
            />
          </div>
        )}
      </div>
    </Card>
  );
}

function PackageEditor({ open, initial, recipes, meta, onClose, onSaved }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (initial) setData({ ...initial });
  }, [initial]);
  if (!open || !data) return null;
  const upd = (patch) => setData((d) => ({ ...d, ...patch }));
  const save = async () => {
    if (!data.name?.trim()) { toast.error(t("hotel.name_required")); return; }
    const payload = {
      name: data.name.trim(),
      description: data.description || "",
      season: data.season || null,
      is_active: data.is_active !== false,
      breakfast: {
        recipe_ids: (data.breakfast?.items || data.breakfast?.recipe_ids || []).map((r) => r.id || r),
        waste_factor_pct: data.breakfast?.waste_factor_pct ?? null,
        per_guest_factor: data.breakfast?.per_guest_factor ?? 1,
      },
      jause: data.jause ? {
        kind: data.jause.kind || "Buffet",
        recipe_ids: (data.jause?.items || data.jause?.recipe_ids || []).map((r) => r.id || r),
        waste_factor_pct: data.jause.waste_factor_pct ?? null,
        per_guest_factor: data.jause.per_guest_factor ?? 1,
        packaging_cost_override: data.jause.packaging_cost_override ?? null,
      } : null,
      dinner: {
        recipe_ids: (data.dinner?.items || data.dinner?.recipe_ids || []).map((r) => r.id || r),
        waste_factor_pct: data.dinner?.waste_factor_pct ?? null,
        per_guest_factor: data.dinner?.per_guest_factor ?? 1,
      },
    };
    setSaving(true);
    try {
      const res = data.id
        ? await api.patch(`/hotel/packages/${data.id}`, payload)
        : await api.post("/hotel/packages", payload);
      toast.success(t("hotel.saved"));
      onSaved(res.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-bg-void/80 backdrop-blur-sm flex justify-end" onClick={onClose}>
      <div className="bg-bg-void border-l border-white/10 w-full max-w-3xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="hotel-package-editor">
        <div className="sticky top-0 bg-bg-void border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-ink-muted font-medium">{data.id ? t("hotel.edit_pkg") : t("hotel.new_pkg")}</div>
            <div className="text-lg font-semibold text-ink-primary">{data.name || t("hotel.new_pkg")}</div>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}><X size={14} /> {t("common.cancel")}</Button>
            <Button onClick={save} disabled={saving} data-testid="hotel-pkg-save">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {t("common.save")}
            </Button>
          </div>
        </div>
        <div className="p-6 space-y-5">
          {/* basics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>{t("hotel.name")}</Label>
              <Input value={data.name || ""} onChange={(e) => upd({ name: e.target.value })} placeholder="Sommerpaket" data-testid="hotel-pkg-name" />
            </div>
            <div>
              <Label>{t("hotel.season")}</Label>
              <Select value={data.season || ""} onChange={(e) => upd({ season: e.target.value || null })} data-testid="hotel-pkg-season">
                <option value="">— {t("common.none")} —</option>
                {meta.seasons.map((s) => <option key={s} value={s}>{t(`hotel.season_${s}`, s)}</option>)}
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>{t("hotel.description")}</Label>
              <Input value={data.description || ""} onChange={(e) => upd({ description: e.target.value })} placeholder={t("hotel.description_ph")} />
            </div>
          </div>

          {/* Totals snapshot if enriched */}
          {data.total_cost_per_guest !== undefined && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Kpi label={t("hotel.kpi_breakfast")} value={fmtEur(data.breakfast?.cost_per_guest)} />
              <Kpi label={t("hotel.kpi_jause")} value={fmtEur(data.jause?.cost_per_guest || 0)} sub={data.jause?.kind ? t(`hotel.jause_${data.jause.kind}`) : t("hotel.no_jause")} />
              <Kpi label={t("hotel.kpi_dinner")} value={fmtEur(data.dinner?.cost_per_guest)} />
              <Kpi label={t("hotel.kpi_total_pp")} value={fmtEur(data.total_cost_per_guest)} tone={data.over_budget ? "err" : "ok"} sub={data.budget_per_guest_day ? `${t("hotel.budget")}: ${fmtEur(data.budget_per_guest_day)}` : t("hotel.no_budget")} />
            </div>
          )}

          {/* Sections */}
          <MealEditor
            title={t("hotel.breakfast")} icon={Coffee} meta={meta} recipes={recipes}
            value={data.breakfast || { recipe_ids: [] }}
            onChange={(v) => upd({ breakfast: v })}
          />
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="m-0">{t("hotel.jause_section")}</Label>
              <label className="text-xs text-ink-secondary flex items-center gap-2">
                <input type="checkbox" checked={!!data.jause} onChange={(e) => upd({ jause: e.target.checked ? { kind: "Buffet", recipe_ids: [], per_guest_factor: 1 } : null })} />
                {t("hotel.jause_enable")}
              </label>
            </div>
            {data.jause && (
              <MealEditor
                title={t("hotel.jause_section")} icon={Cookie} meta={meta} recipes={recipes}
                value={data.jause}
                onChange={(v) => upd({ jause: v })}
                allowJause
              />
            )}
          </div>
          <MealEditor
            title={t("hotel.dinner")} icon={UtensilsCrossed} meta={meta} recipes={recipes}
            value={data.dinner || { recipe_ids: [] }}
            onChange={(v) => upd({ dinner: v })}
          />
        </div>
      </div>
    </div>
  );
}

function PackagesTab({ packages, recipes, meta, onReload, onEdit }) {
  const { t } = useTranslation();
  const handleDelete = async (p) => {
    if (!window.confirm(t("hotel.delete_pkg_confirm", { name: p.name }))) return;
    try {
      await api.delete(`/hotel/packages/${p.id}`);
      toast.success(t("hotel.deleted"));
      onReload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (packages.length === 0) {
    return <EmptyState icon={Package} title={t("hotel.no_packages")} description={t("hotel.no_packages_sub")} />;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="hotel-packages-grid">
      {packages.map((p) => {
        const SeasonIcon = SEASON_ICON[p.season];
        return (
          <Card key={p.id} className="p-4" data-testid={`hotel-pkg-${p.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  {SeasonIcon && <SeasonIcon size={11} className="text-brand" />}
                  {p.season && <Badge tone="brand">{t(`hotel.season_${p.season}`)}</Badge>}
                  {!p.is_active && <Badge tone="default">{t("hotel.inactive")}</Badge>}
                  {p.over_budget && <Badge tone="err">{t("hotel.over_budget")}</Badge>}
                </div>
                <div className="text-base font-semibold text-ink-primary truncate">{p.name}</div>
                {p.description && <div className="text-xs text-ink-muted truncate">{p.description}</div>}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className="bg-bg-void rounded-md px-2 py-1.5">
                <div className="text-[9px] uppercase tracking-wider text-ink-muted">FB</div>
                <div className="text-xs num">{fmtEur(p.breakfast?.cost_per_guest)}</div>
              </div>
              <div className="bg-bg-void rounded-md px-2 py-1.5">
                <div className="text-[9px] uppercase tracking-wider text-ink-muted">Jause</div>
                <div className="text-xs num">{fmtEur(p.jause?.cost_per_guest || 0)}</div>
              </div>
              <div className="bg-bg-void rounded-md px-2 py-1.5">
                <div className="text-[9px] uppercase tracking-wider text-ink-muted">Abend</div>
                <div className="text-xs num">{fmtEur(p.dinner?.cost_per_guest)}</div>
              </div>
            </div>
            <div className={`flex items-center justify-between p-2 rounded-md mb-3 ${p.over_budget ? "bg-err/10" : "bg-ok/5"}`}>
              <div className="text-[10px] uppercase tracking-wider text-ink-muted">{t("hotel.kpi_total_pp")}</div>
              <div className="num text-sm font-semibold">{fmtEur(p.total_cost_per_guest)}</div>
            </div>
            <div className="flex gap-1.5 pt-2 border-t border-white/5">
              <Button variant="ghost" size="sm" onClick={() => onEdit(p)} data-testid={`hotel-edit-${p.id}`}><Edit3 size={12} /> {t("common.edit")}</Button>
              <div className="flex-1" />
              <Button variant="ghost" size="sm" onClick={() => handleDelete(p)} data-testid={`hotel-delete-${p.id}`}><Trash2 size={12} className="text-err" /></Button>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// ============================================================
//  Occupancy tab
// ============================================================
function OccupancyTab({ meta }) {
  const { t } = useTranslation();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [presetSeason, setPresetSeason] = useState("Summer");

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/hotel/occupancy?year=${year}&month=${month}`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const updateDay = (idx, guests) => {
    const next = [...data.days];
    next[idx] = { ...next[idx], guests: Math.max(0, parseInt(guests || 0, 10)) };
    setData({ ...data, days: next, total_guest_nights: next.reduce((s, x) => s + (x.guests || 0), 0) });
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/hotel/occupancy", { year, month, days: data.days, notes: data.notes || "" });
      setData(r.data);
      toast.success(t("hotel.occupancy_saved"));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const applyPreset = async () => {
    if (!window.confirm(t("hotel.preset_confirm", { season: t(`hotel.season_${presetSeason}`) }))) return;
    try {
      const r = await api.post("/hotel/occupancy/preset", { year, month, season: presetSeason, overwrite: true });
      setData(r.data);
      toast.success(t("hotel.preset_applied"));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return <div className="flex items-center justify-center py-12"><Spinner size={20} /></div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label>{t("hotel.year")}</Label>
          <Input type="number" min="2020" max="2100" value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-24" data-testid="occ-year" />
        </div>
        <div>
          <Label>{t("hotel.month")}</Label>
          <Select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="w-32" data-testid="occ-month">
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString("de", { month: "long" })}</option>
            ))}
          </Select>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label>{t("hotel.preset_season")}</Label>
            <Select value={presetSeason} onChange={(e) => setPresetSeason(e.target.value)} className="w-32" data-testid="occ-preset-season">
              {meta.seasons.map((s) => <option key={s} value={s}>{t(`hotel.season_${s}`)}</option>)}
            </Select>
          </div>
          <Button variant="secondary" onClick={applyPreset} data-testid="occ-preset-apply">{t("hotel.apply_preset")}</Button>
        </div>
        <div className="flex-1" />
        <Button onClick={save} disabled={saving} data-testid="occ-save">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {t("common.save")}
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Kpi label={t("hotel.kpi_total_nights")} value={data.total_guest_nights} sub={`${data.days_in_month} ${t("hotel.days")}`} testId="occ-kpi-total" />
        <Kpi label={t("hotel.kpi_avg_guests")} value={Math.round((data.total_guest_nights / data.days_in_month) * 10) / 10} />
        <Kpi label={t("hotel.kpi_peak")} value={data.days.reduce((m, d) => Math.max(m, d.guests || 0), 0)} />
        <Kpi label={t("hotel.kpi_zero_days")} value={data.days.filter((d) => (d.guests || 0) === 0).length} />
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-7 gap-1.5" data-testid="occ-grid">
          {data.days.map((d, idx) => (
            <div key={d.day} className="text-center">
              <div className="text-[10px] text-ink-muted mb-0.5">{d.day}</div>
              <Input
                type="number" min="0" value={d.guests}
                onChange={(e) => updateDay(idx, e.target.value)}
                className="text-center px-1 py-1 num text-sm"
                data-testid={`occ-day-${d.day}`}
              />
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ============================================================
//  Group calculator tab
// ============================================================
function GroupTab({ packages }) {
  const { t } = useTranslation();
  const [pkgId, setPkgId] = useState("");
  const [size, setSize] = useState(20);
  const [markup, setMarkup] = useState("");
  const [result, setResult] = useState(null);
  const [calc, setCalc] = useState(false);

  const submit = async () => {
    if (!pkgId) { toast.error(t("hotel.choose_pkg")); return; }
    setCalc(true);
    try {
      const body = { package_id: pkgId, group_size: Number(size) };
      if (markup) body.target_markup_pct = Number(markup);
      const r = await api.post("/hotel/group-calc", body);
      setResult(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCalc(false); }
  };

  return (
    <div className="space-y-4">
      <Card className="p-5 space-y-4" data-testid="hotel-group-form">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label>{t("hotel.package")}</Label>
            <Select value={pkgId} onChange={(e) => setPkgId(e.target.value)} data-testid="group-pkg">
              <option value="">— {t("common.select")} —</option>
              {packages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </div>
          <div>
            <Label>{t("hotel.group_size")}</Label>
            <Input type="number" min="1" value={size} onChange={(e) => setSize(e.target.value)} data-testid="group-size" />
          </div>
          <div>
            <Label>{t("hotel.markup_pct_opt")}</Label>
            <Input type="number" min="0" max="500" value={markup} onChange={(e) => setMarkup(e.target.value)} placeholder={t("hotel.markup_default")} data-testid="group-markup" />
          </div>
        </div>
        <Button onClick={submit} disabled={calc} data-testid="group-calc-btn">
          {calc ? <Loader2 size={14} className="animate-spin" /> : <Bus size={14} />}
          {t("hotel.calc_group")}
        </Button>
      </Card>

      {result && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="hotel-group-result">
          <Kpi label={t("hotel.cost_per_person")} value={fmtEur(result.package.total_cost_per_guest)} />
          <Kpi label={t("hotel.total_cost")} value={fmtEur(result.total_cost)} sub={`${result.group_size} ${t("hotel.persons")}`} />
          <Kpi label={t("hotel.suggested_pp")} value={fmtEur(result.suggested_price_per_person)} tone="ok" />
          <Kpi label={t("hotel.suggested_db")} value={fmtEur(result.estimated_contribution_margin)} tone="ok" sub={`${t("hotel.revenue")}: ${fmtEur(result.suggested_revenue)}`} />
        </div>
      )}
    </div>
  );
}

// ============================================================
//  Projection tab
// ============================================================
function ProjectionTab({ packages }) {
  const { t } = useTranslation();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [pkgId, setPkgId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (packages.length && !pkgId) setPkgId(packages[0].id); }, [packages, pkgId]);

  const load = useCallback(async () => {
    if (!pkgId) return;
    setLoading(true);
    try {
      const r = await api.get(`/hotel/projection?year=${year}&month=${month}&package_id=${pkgId}`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); setData(null); }
    finally { setLoading(false); }
  }, [year, month, pkgId]);

  useEffect(() => { load(); }, [load]);

  if (packages.length === 0) {
    return <EmptyState icon={BarChart3} title={t("hotel.no_packages")} description={t("hotel.no_packages_for_projection")} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label>{t("hotel.year")}</Label>
          <Input type="number" min="2020" max="2100" value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-24" data-testid="proj-year" />
        </div>
        <div>
          <Label>{t("hotel.month")}</Label>
          <Select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="w-32" data-testid="proj-month">
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString("de", { month: "long" })}</option>
            ))}
          </Select>
        </div>
        <div>
          <Label>{t("hotel.package")}</Label>
          <Select value={pkgId} onChange={(e) => setPkgId(e.target.value)} className="w-56" data-testid="proj-pkg">
            {packages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
        </div>
      </div>

      {loading ? <div className="flex items-center justify-center py-12"><Spinner size={20} /></div> : data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="hotel-projection-kpis">
            <Kpi label={t("hotel.kpi_total_nights")} value={data.total_guest_nights} />
            <Kpi label={t("hotel.kpi_cost_pp")} value={fmtEur(data.package.cost_per_guest)} />
            <Kpi label={t("hotel.kpi_projected_cost")} value={fmtEur(data.projected_cost)} tone={data.over_budget ? "err" : "default"} />
            <Kpi
              label={t("hotel.kpi_budget")}
              value={data.projected_budget != null ? fmtEur(data.projected_budget) : "—"}
              sub={data.delta != null ? `${data.delta >= 0 ? "+" : ""}${fmtEur(data.delta)}` : t("hotel.no_budget_set")}
              tone={data.over_budget ? "err" : "ok"}
            />
          </div>
          {data.over_budget && (
            <div className="flex items-center gap-2 px-4 py-3 bg-err/10 border border-err/30 rounded-lg text-sm text-err" data-testid="hotel-over-budget">
              <AlertTriangle size={14} /> {t("hotel.over_budget_warning", { delta: fmtEur(data.delta) })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ============================================================
//  Page
// ============================================================
// ============================================================
//  Feature 5 — Hotel Gesamtkosten-Verwaltung
// ============================================================
const COST_GROUPS_COLORS = { "Immobilie": "#a78bfa", "Personal": "#60a5fa", "F&B": "#f59e0b", "Marketing": "#34d399", "Betrieb": "#fb7185", "Finanzierung": "#94a3b8" };
const STAFF_KEYS = ["staff_reception", "staff_housekeeping", "staff_kitchen", "staff_service", "staff_management", "staff_seasonal"];

function CostBar({ value, max, color }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="h-2 bg-white/5 rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

function CostWaterfall({ breakdown }) {
  if (!breakdown?.length) return <div className="text-xs text-ink-muted text-center py-6">—</div>;
  const total = breakdown.reduce((s, b) => s + b.value, 0);
  const max = Math.max(...breakdown.map((b) => b.value));
  const sorted = [...breakdown].sort((a, b) => b.value - a.value);
  return (
    <div className="space-y-1.5">
      {sorted.map((b, i) => (
        <div key={i} className="grid grid-cols-[140px_1fr_90px] items-center gap-3">
          <div className="text-xs text-ink-secondary truncate">{b.label}</div>
          <CostBar value={b.value} max={max} color={COST_GROUPS_COLORS[b.group] || "#94a3b8"} />
          <div className="text-xs num text-ink-primary text-right tabular-nums">
            {fmtEur(b.value)}
            <span className="text-[10px] text-ink-muted ml-1">({((b.value / total) * 100).toFixed(1)}%)</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function GroupDonut({ groups }) {
  if (!groups?.length) return null;
  const total = groups.reduce((s, g) => s + g.value, 0);
  if (total <= 0) return null;
  const cx = 80, cy = 80, r = 64, stroke = 16;
  const C = 2 * Math.PI * r;
  let acc = 0;
  return (
    <svg viewBox="0 0 160 160" className="w-40 h-40">
      <circle cx={cx} cy={cy} r={r} fill="transparent" stroke="rgba(255,255,255,0.04)" strokeWidth={stroke} />
      {groups.map((g, i) => {
        const frac = g.value / total;
        const dash = C * frac;
        const offset = -(acc * C);
        acc += frac;
        return (
          <circle key={i} cx={cx} cy={cy} r={r} fill="transparent"
            stroke={COST_GROUPS_COLORS[g.name] || "#94a3b8"} strokeWidth={stroke}
            strokeDasharray={`${dash} ${C - dash}`}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        );
      })}
    </svg>
  );
}

function ProfitMarginSlider({ breakEven, onPriceChange }) {
  const { t } = useTranslation();
  const [margin, setMargin] = useState(20);
  const price = breakEven > 0 ? breakEven / (1 - margin / 100) : 0;
  useEffect(() => { onPriceChange?.(price); }, [price]); // eslint-disable-line
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-ink-muted">{t("hotel.costs.margin")}: {margin}%</span>
        <span className="num text-base text-brand">{fmtEur(price)} / {t("hotel.costs.room_night")}</span>
      </div>
      <input
        type="range" min={0} max={70} value={margin}
        onChange={(e) => setMargin(Number(e.target.value))}
        className="w-full accent-brand"
        data-testid="hotel-costs-margin-slider"
      />
    </div>
  );
}

function StaffRow({ k, line, onChange }) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-[1fr_70px_120px_90px] items-end gap-2" data-testid={`staff-row-${k}`}>
      <div>
        <Label className="text-[10px]">{t(`hotel.costs.${k}`)}</Label>
        <Input value={line.name || ""} onChange={(e) => onChange({ ...line, name: e.target.value })} placeholder={t(`hotel.costs.${k}_ph`, "")} />
      </div>
      <div>
        <Label className="text-[10px]">FTE</Label>
        <Input type="number" step="0.1" value={line.fte || 0} onChange={(e) => onChange({ ...line, fte: Number(e.target.value) || 0 })} data-testid={`staff-${k}-fte`} />
      </div>
      <div>
        <Label className="text-[10px]">{t("hotel.costs.monthly_cost")}</Label>
        <Input type="number" step="0.01" value={line.monthly_cost || 0} onChange={(e) => onChange({ ...line, monthly_cost: Number(e.target.value) || 0 })} data-testid={`staff-${k}-cost`} />
      </div>
      <div>
        <Label className="text-[10px]">{t("hotel.costs.months_active")}</Label>
        <Input type="number" min={0} max={12} value={line.months_active ?? 12} onChange={(e) => onChange({ ...line, months_active: Number(e.target.value) || 12 })} />
      </div>
    </div>
  );
}

function HotelCostsTab() {
  const { t } = useTranslation();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/cost-profile/hotel");
        setDoc(data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const patch = (delta) => setDoc((d) => ({ ...d, ...delta }));
  const patchStaff = (k, line) => setDoc((d) => ({ ...d, [k]: line }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...doc };
      delete payload.calc;
      delete payload.configured;
      delete payload.updated_at;
      const { data } = await api.put("/cost-profile/hotel", payload);
      setDoc(data);
      toast.success(t("hotel.costs.saved"));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const recompute = async () => {
    try {
      const { data } = await api.post("/cost-profile/hotel/projection", doc);
      patch({ calc: data });
    } catch (e) { /* ignore */ }
  };

  // Debounced live recompute
  useEffect(() => {
    if (!doc) return;
    const t = setTimeout(recompute, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(doc && {...doc, calc: undefined})]);

  if (loading || !doc) return <Spinner size={20} />;
  const calc = doc.calc || {};

  return (
    <div className="space-y-6 fade-in" data-testid="hotel-costs-tab">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="hotel-costs-kpis">
        <Kpi label={t("hotel.costs.kpi_total_annual")} value={fmtEur(calc.total_annual_cost)} testId="kpi-total" />
        <Kpi label={t("hotel.costs.kpi_cost_par")} value={fmtEur(calc.cost_per_available_room_night)} sub={t("hotel.costs.kpi_cost_par_sub")} testId="kpi-cost-par" />
        <Kpi label={t("hotel.costs.kpi_cost_per_occ")} value={fmtEur(calc.cost_per_occupied_room_night)} sub={t("hotel.costs.kpi_cost_per_occ_sub")} testId="kpi-cost-occ" />
        <Kpi label={t("hotel.costs.kpi_break_even")} value={fmtEur(calc.break_even_room_price)} tone={calc.break_even_room_price > 0 ? "warn" : "default"} testId="kpi-break-even" />
        <Kpi label={t("hotel.costs.kpi_revpar")} value={fmtEur(calc.revpar_target)} sub={`ADR ${fmtEur(calc.target_adr_eur)}`} testId="kpi-revpar" />
      </div>

      {/* Two-column layout: editor + dashboard */}
      <div className="grid lg:grid-cols-[1fr_400px] gap-6">
        {/* Editor */}
        <div className="space-y-4">
          {/* Immobilie */}
          <Card className="p-4">
            <div className="text-sm font-semibold mb-3 flex items-center gap-2"><Building2_icon /> {t("hotel.costs.group_immobilie")}</div>
            <div className="grid grid-cols-2 gap-3">
              {["rent", "nebenkosten", "building_insurance", "property_tax"].map((k) => (
                <div key={k}>
                  <Label>{t(`hotel.costs.${k}`)}</Label>
                  <Input type="number" step="0.01" value={doc[k] || 0} onChange={(e) => patch({ [k]: Number(e.target.value) || 0 })} data-testid={`hotel-${k}`} />
                </div>
              ))}
            </div>
          </Card>

          {/* Personal */}
          <Card className="p-4">
            <div className="text-sm font-semibold mb-3 flex items-center gap-2"><Users size={14} className="text-brand" /> {t("hotel.costs.group_personal")}</div>
            <div className="space-y-2">
              {STAFF_KEYS.map((k) => (
                <StaffRow key={k} k={k} line={doc[k] || {}} onChange={(l) => patchStaff(k, l)} />
              ))}
            </div>
          </Card>

          {/* F&B */}
          <Card className="p-4">
            <div className="text-sm font-semibold mb-3 flex items-center gap-2"><UtensilsCrossed size={14} className="text-brand" /> {t("hotel.costs.group_fnb")}</div>
            <div className="grid grid-cols-3 gap-3">
              {["food_budget", "beverage_budget", "kitchen_leasing"].map((k) => (
                <div key={k}>
                  <Label>{t(`hotel.costs.${k}`)}</Label>
                  <Input type="number" step="0.01" value={doc[k] || 0} onChange={(e) => patch({ [k]: Number(e.target.value) || 0 })} data-testid={`hotel-${k}`} />
                </div>
              ))}
            </div>
          </Card>

          {/* Marketing */}
          <Card className="p-4">
            <div className="text-sm font-semibold mb-3 flex items-center gap-2"><TrendingUp size={14} className="text-brand" /> {t("hotel.costs.group_marketing")}</div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>{t("hotel.costs.booking_commission_pct")} (%)</Label>
                <Input type="number" step="0.1" value={doc.booking_commission_pct || 0} onChange={(e) => patch({ booking_commission_pct: Number(e.target.value) || 0 })} data-testid="hotel-booking-pct" />
              </div>
              <div>
                <Label>{t("hotel.costs.website_cost")}</Label>
                <Input type="number" step="0.01" value={doc.website_cost || 0} onChange={(e) => patch({ website_cost: Number(e.target.value) || 0 })} />
              </div>
              <div>
                <Label>{t("hotel.costs.advertising")}</Label>
                <Input type="number" step="0.01" value={doc.advertising || 0} onChange={(e) => patch({ advertising: Number(e.target.value) || 0 })} />
              </div>
            </div>
          </Card>

          {/* Betrieb */}
          <Card className="p-4">
            <div className="text-sm font-semibold mb-3 flex items-center gap-2"><Receipt size={14} className="text-brand" /> {t("hotel.costs.group_betrieb")}</div>
            <div className="grid grid-cols-4 gap-3">
              {["laundry", "cleaning", "software_tools"].map((k) => (
                <div key={k}>
                  <Label>{t(`hotel.costs.${k}`)}</Label>
                  <Input type="number" step="0.01" value={doc[k] || 0} onChange={(e) => patch({ [k]: Number(e.target.value) || 0 })} />
                </div>
              ))}
              <div>
                <Label>{t("hotel.costs.maintenance_reserve_pct")} (%)</Label>
                <Input type="number" step="0.1" value={doc.maintenance_reserve_pct || 0} onChange={(e) => patch({ maintenance_reserve_pct: Number(e.target.value) || 0 })} />
              </div>
            </div>
          </Card>

          {/* Finanzierung */}
          <Card className="p-4">
            <div className="text-sm font-semibold mb-3 flex items-center gap-2"><Wallet size={14} className="text-brand" /> {t("hotel.costs.group_finanzierung")}</div>
            <div className="grid grid-cols-3 gap-3">
              {["loans", "leasing", "tax_consulting"].map((k) => (
                <div key={k}>
                  <Label>{t(`hotel.costs.${k}`)}</Label>
                  <Input type="number" step="0.01" value={doc[k] || 0} onChange={(e) => patch({ [k]: Number(e.target.value) || 0 })} />
                </div>
              ))}
            </div>
          </Card>

          {/* Calc inputs */}
          <Card className="p-4">
            <div className="text-sm font-semibold mb-3 flex items-center gap-2"><Target size={14} className="text-brand" /> {t("hotel.costs.group_planning")}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <Label>{t("hotel.costs.rooms_total")}</Label>
                <Input type="number" value={doc.rooms_total || 0} onChange={(e) => patch({ rooms_total: Number(e.target.value) || 0 })} data-testid="hotel-rooms" />
              </div>
              <div>
                <Label>{t("hotel.costs.high_season_months")}</Label>
                <Input type="number" min={0} max={12} value={doc.high_season_months || 6} onChange={(e) => patch({ high_season_months: Number(e.target.value) || 0 })} />
              </div>
              <div>
                <Label>{t("hotel.costs.expected_occupancy_pct")} (%)</Label>
                <Input type="number" min={0} max={100} value={doc.expected_occupancy_pct || 60} onChange={(e) => patch({ expected_occupancy_pct: Number(e.target.value) || 0 })} />
              </div>
              <div>
                <Label>{t("hotel.costs.target_adr_eur")}</Label>
                <Input type="number" step="0.01" value={doc.target_adr_eur || 0} onChange={(e) => patch({ target_adr_eur: Number(e.target.value) || 0 })} data-testid="hotel-adr" />
              </div>
            </div>
          </Card>

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} data-testid="hotel-costs-save">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {t("hotel.costs.save")}
            </Button>
          </div>
        </div>

        {/* Sidebar dashboard */}
        <div className="space-y-4 lg:sticky lg:top-4 self-start">
          <Card className="p-4">
            <div className="text-sm font-semibold mb-2">{t("hotel.costs.seasonal_breakdown")}</div>
            <div className="grid grid-cols-2 gap-2 text-center">
              <div className="bg-bg-void rounded-md py-3">
                <div className="text-[10px] uppercase tracking-wider text-ink-muted flex items-center justify-center gap-1"><Sun size={11} /> {t("hotel.costs.high_season")}</div>
                <div className="num text-lg text-ink-primary">{fmtEur(calc.high_season_monthly)}</div>
                <div className="text-[10px] text-ink-muted">/{t("hotel.costs.month")}</div>
              </div>
              <div className="bg-bg-void rounded-md py-3">
                <div className="text-[10px] uppercase tracking-wider text-ink-muted flex items-center justify-center gap-1"><Snowflake size={11} /> {t("hotel.costs.low_season")}</div>
                <div className="num text-lg text-ink-primary">{fmtEur(calc.low_season_monthly)}</div>
                <div className="text-[10px] text-ink-muted">/{t("hotel.costs.month")}</div>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="text-sm font-semibold mb-3">{t("hotel.costs.by_group")}</div>
            <div className="flex items-center gap-3">
              <GroupDonut groups={calc.by_group} />
              <div className="space-y-1 flex-1">
                {(calc.by_group || []).map((g) => (
                  <div key={g.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full" style={{ background: COST_GROUPS_COLORS[g.name] || "#94a3b8" }} />
                      <span className="text-ink-secondary">{g.name}</span>
                    </div>
                    <span className="num text-ink-primary">{fmtEur(g.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="text-sm font-semibold mb-3 flex items-center gap-2"><PiggyBank size={14} className="text-ok" /> {t("hotel.costs.profit_calc")}</div>
            <ProfitMarginSlider breakEven={calc.break_even_room_price || 0} />
            <div className="mt-3 grid grid-cols-3 gap-1.5 text-center pt-3 border-t border-white/5">
              <div>
                <div className="text-[10px] text-ink-muted">10%</div>
                <div className="num text-xs text-ok">{fmtEur(calc.price_for_10pct_margin)}</div>
              </div>
              <div>
                <div className="text-[10px] text-ink-muted">20%</div>
                <div className="num text-xs text-ok">{fmtEur(calc.price_for_20pct_margin)}</div>
              </div>
              <div>
                <div className="text-[10px] text-ink-muted">30%</div>
                <div className="num text-xs text-ok">{fmtEur(calc.price_for_30pct_margin)}</div>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="text-sm font-semibold mb-2">{t("hotel.costs.annual_projection")}</div>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-ink-muted">{t("hotel.costs.revenue_target")}</span><span className="num text-ink-primary">{fmtEur(calc.expected_annual_revenue)}</span></div>
              <div className="flex justify-between"><span className="text-ink-muted">{t("hotel.costs.total_cost")}</span><span className="num text-ink-primary">−{fmtEur(calc.total_annual_cost)}</span></div>
              <div className="flex justify-between border-t border-white/5 pt-1.5">
                <span className="text-ink-primary font-medium">{t("hotel.costs.profit_loss")}</span>
                <span className={`num font-medium ${calc.annual_profit >= 0 ? "text-ok" : "text-err"}`}>{fmtEur(calc.annual_profit)}</span>
              </div>
              <div className="flex justify-between text-xs"><span className="text-ink-muted">{t("hotel.costs.profit_margin")}</span><span className={`num ${calc.profit_pct >= 0 ? "text-ok" : "text-err"}`}>{calc.profit_pct?.toFixed(1)}%</span></div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="text-sm font-semibold mb-2 flex items-center gap-2"><UtensilsCrossed size={14} className="text-brand" /> {t("hotel.costs.fnb_per_guest")}</div>
            <div className="num text-2xl text-ink-primary">{fmtEur(calc.fnb_cost_per_guest_day)}</div>
            <div className="text-[11px] text-ink-muted mt-1">{t("hotel.costs.fnb_per_guest_hint")}</div>
          </Card>
        </div>
      </div>

      {/* Waterfall */}
      <Card className="p-5">
        <div className="text-sm font-semibold mb-4 flex items-center gap-2"><BarChart3 size={14} className="text-brand" /> {t("hotel.costs.waterfall")}</div>
        <CostWaterfall breakdown={calc.breakdown} />
      </Card>
    </div>
  );
}

// ============================================================
//  Feature 8 — Seasonal Room Categories
// ============================================================
const SEASONS_LIST = ["Hochsaison Winter", "Nebensaison Frühling", "Hochsaison Sommer", "Nebensaison Herbst"];

function HotelRoomsTab() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/hotel/room-categories");
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    try {
      if (form.id) {
        await api.put(`/hotel/room-categories/${form.id}`, form);
      } else {
        await api.post("/hotel/room-categories", form);
      }
      toast.success(t("hotel.rooms.saved"));
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm(t("hotel.rooms.confirm_delete"))) return;
    try {
      await api.delete(`/hotel/room-categories/${id}`);
      toast.success(t("hotel.rooms.deleted"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading || !data) return <Spinner size={20} />;

  return (
    <div className="space-y-4 fade-in" data-testid="hotel-rooms-tab">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs text-ink-muted">{t("hotel.rooms.current_season")}: <span className="text-brand font-medium">{data.current_season}</span></div>
          <h2 className="text-lg font-semibold">{t("hotel.rooms.title")}</h2>
        </div>
        <Button onClick={() => setEditing({ name: "", base_price_eur: 100, seasonal_prices: {}, fnb_pct: 30 })} data-testid="room-new">
          <Plus size={14} /> {t("hotel.rooms.new")}
        </Button>
      </div>

      {data.items.length === 0 ? (
        <EmptyState icon={BedDouble} title={t("hotel.rooms.empty")} description={t("hotel.rooms.empty_sub")} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="rooms-grid">
          {data.items.map((r) => (
            <Card key={r.id} className="p-4" data-testid={`room-card-${r.id}`}>
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-semibold text-ink-primary">{r.name}</h3>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(r)} data-testid={`room-edit-${r.id}`}><Edit3 size={12} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => del(r.id)}><Trash2 size={12} className="text-err" /></Button>
                </div>
              </div>
              <div className="flex items-baseline gap-2 mb-3">
                <div className="num text-2xl font-medium text-brand">{fmtEur(r.current_price)}</div>
                <span className="text-xs text-ink-muted">/ {t("hotel.rooms.night")}</span>
              </div>
              <div className="text-xs text-ink-muted space-y-1">
                <div className="flex justify-between"><span>{t("hotel.rooms.base_price")}</span><span className="num">{fmtEur(r.base_price_eur)}</span></div>
                <div className="flex justify-between"><span>{t("hotel.rooms.fnb_pct")}</span><span>{r.fnb_pct}%</span></div>
                <div className="flex justify-between border-t border-white/5 pt-1.5 mt-1.5">
                  <span>{t("hotel.rooms.fnb_budget")}</span>
                  <span className="num text-ok">{fmtEur(r.fnb_budget_per_guest_day)}</span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <RoomEditor initial={editing} onClose={() => setEditing(null)} onSave={save} seasons={data.seasons} />
      )}
    </div>
  );
}

function RoomEditor({ initial, onClose, onSave, seasons }) {
  const { t } = useTranslation();
  const [f, setF] = useState({
    ...initial,
    seasonal_prices: { ...(initial.seasonal_prices || {}) },
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!f.name.trim()) { toast.error(t("hotel.rooms.name_required")); return; }
    setSaving(true);
    try {
      await onSave({
        ...(f.id ? { id: f.id } : {}),
        name: f.name.trim(),
        base_price_eur: Number(f.base_price_eur) || 0,
        seasonal_prices: f.seasonal_prices,
        fnb_pct: Number(f.fnb_pct) || 30,
      });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-bg-void/80 backdrop-blur-sm flex justify-end" onClick={onClose}>
      <div className="bg-bg-void border-l border-white/10 w-full max-w-md h-full overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="room-editor">
        <header className="sticky top-0 bg-bg-void border-b border-white/10 px-5 py-3 flex items-center justify-between">
          <h2 className="font-semibold">{f.id ? t("hotel.rooms.edit") : t("hotel.rooms.new")}</h2>
          <Button variant="ghost" size="sm" onClick={onClose}><X size={14} /></Button>
        </header>
        <div className="p-5 space-y-3">
          <div>
            <Label>{t("hotel.rooms.name_label")}</Label>
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="room-name" placeholder="Doppelzimmer" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("hotel.rooms.base_price")} (€)</Label>
              <Input type="number" step="0.01" value={f.base_price_eur} onChange={(e) => setF({ ...f, base_price_eur: e.target.value })} data-testid="room-base-price" />
            </div>
            <div>
              <Label>{t("hotel.rooms.fnb_pct")} (%)</Label>
              <Input type="number" step="0.1" value={f.fnb_pct} onChange={(e) => setF({ ...f, fnb_pct: e.target.value })} data-testid="room-fnb-pct" />
            </div>
          </div>
          <div className="pt-2">
            <Label>{t("hotel.rooms.seasonal_prices")}</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {seasons.map((s) => (
                <div key={s} className="space-y-1" data-testid={`room-season-wrap-${s.replace(/\s+/g, "-")}`}>
                  <div className="text-[10px] uppercase tracking-wider text-ink-muted leading-tight whitespace-nowrap overflow-hidden text-ellipsis" title={s}>
                    {s}
                  </div>
                  <Input
                    type="number" step="0.01"
                    placeholder="Basispreis"
                    value={f.seasonal_prices[s] || ""}
                    onChange={(e) => setF({ ...f, seasonal_prices: { ...f.seasonal_prices, [s]: Number(e.target.value) || 0 } })}
                    className="w-full num"
                    data-testid={`room-season-${s.replace(/\s+/g, "-")}`}
                  />
                </div>
              ))}
            </div>
          </div>
          <Button onClick={submit} disabled={saving} className="w-full" data-testid="room-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {t("hotel.rooms.save")}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Local shim because Building2 isn't imported into this file
function Building2_icon() {
  return <Receipt size={14} className="text-brand" />;
}

const TABS = ["packages", "occupancy", "groups", "projection", "costs", "rooms"];

export default function HotelPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("packages");
  const [packages, setPackages] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [meta, setMeta] = useState({ jause_kinds: [], seasons: [], season_presets: {}, defaults: {} });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, r, m] = await Promise.all([
        api.get("/hotel/packages"),
        api.get("/recipes"),
        api.get("/hotel/meta"),
      ]);
      setPackages(p.data.items || []);
      setRecipes(r.data.items || []);
      setMeta(m.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleNew = () => setEditing({
    name: "", season: null, is_active: true, description: "",
    breakfast: { recipe_ids: [], per_guest_factor: 1 },
    jause: null,
    dinner: { recipe_ids: [], per_guest_factor: 1 },
  });

  return (
    <div className="space-y-6 fade-in" data-testid="hotel-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-brand mb-1 flex items-center gap-2">
            <Hotel size={12} /> {t("hotel.kicker")}
          </div>
          <h1 className="text-2xl font-semibold text-ink-primary">{t("hotel.title")}</h1>
          <p className="text-sm text-ink-secondary mt-1 max-w-2xl">{t("hotel.subtitle")}</p>
        </div>
        {tab === "packages" && (
          <Button onClick={handleNew} data-testid="hotel-new-pkg"><Plus size={14} /> {t("hotel.new_pkg")}</Button>
        )}
      </header>

      <div className="flex flex-wrap gap-1 border-b border-white/10" data-testid="hotel-tabs">
        {TABS.map((tb) => {
          const Icon = { packages: Package, occupancy: CalendarDays, groups: Bus, projection: BarChart3, costs: Receipt, rooms: BedDouble }[tb];
          return (
            <button
              key={tb}
              onClick={() => setTab(tb)}
              className={`px-3 py-2 text-sm flex items-center gap-1.5 border-b-2 transition-colors ${tab === tb ? "border-brand text-ink-primary" : "border-transparent text-ink-secondary hover:text-ink-primary"}`}
              data-testid={`hotel-tab-${tb}`}
            >
              <Icon size={13} /> {t(`hotel.tab_${tb}`)}
            </button>
          );
        })}
      </div>

      {loading ? <div className="flex items-center justify-center py-20"><Spinner size={24} /></div> : (
        <>
          {tab === "packages" && (
            <PackagesTab packages={packages} recipes={recipes} meta={meta} onReload={load} onEdit={setEditing} />
          )}
          {tab === "occupancy" && <OccupancyTab meta={meta} />}
          {tab === "groups" && <GroupTab packages={packages} />}
          {tab === "projection" && <ProjectionTab packages={packages} />}
          {tab === "costs" && <HotelCostsTab />}
          {tab === "rooms" && <HotelRoomsTab />}
        </>
      )}

      <PackageEditor
        open={editing != null}
        initial={editing}
        recipes={recipes}
        meta={meta}
        onClose={() => setEditing(null)}
        onSaved={(p) => { setEditing(p); load(); }}
      />
    </div>
  );
}
